import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { CommonModule, formatCurrency, formatDate, formatPercent } from '@angular/common';
import { SbGenericTypeSelectComponent } from '../../../pages/authenticated/accounting/components/sb-generic-type-select/sb-generic-type-select.component';
import { TablerIconsModule } from 'angular-tabler-icons';
import {
    SbKeyMetric,
    SbKeyMetricsComponent
} from '../sb-key-metrics/sb-key-metrics.component';
import {
    BehaviorSubject,
    combineLatest,
    defer,
    iif,
    Observable,
    of,
    ReplaySubject,
    Subject,
    switchMap
} from 'rxjs';
import { AccountTypeEnum, Acct } from '../../state/accts/acct.model';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    startWith,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';
import { combineQueries, distinctUntilArrayItemChanged } from '@datorama/akita';
import { UserSettingsQuery } from '../../../features/user-settings/state/user-settings.query';
import { SharedModule } from '../../shared.module';
import { ChartDataService } from './state/chart-data.service';
import { FormatCurrencyPipe } from '../../pipes/format-currency.pipe';
import { SbPerformanceChartComponent } from '../sb-performance-chart/sb-performance-chart.component';
import {
    Badge,
    BadgesChangeEvent,
    SbBadgesComponent
} from '../sb-badges/sb-badges.component';
import { UiQuery } from '../../state/ui/ui.query';
import {
    fadeInOut,
    fadeInOutModal,
    slideInOut
} from '../../animations/fade-in-out.animation';
import { getPeriodsMap, Period } from '../../utils/chart-utils';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { BenchmarkQuery } from '../../state/benchmark/benchmark.query';
import { FormControl } from '@angular/forms';
import { AcctsQuery } from '../../state/accts/accts.query';
import { Benchmark } from '../../state/benchmark/benchmark.model';
import { LegalEntity } from '../../../pages/admin/state/legal-entities/legal-entity.model';
import { AccountIssuer } from '../../../pages/admin/state/account-issuers/account-issuer.model';
import { AccountIssuersQuery } from '../../../pages/admin/state/account-issuers/account-issuers.query';
import { LegalEntityQuery } from '../../../pages/admin/state/legal-entities/legal-entity.query';
import { SbProTipComponent } from '../sb-pro-tip/sb-pro-tip.component';
import { fromCamelCaseToSentence } from '../../bb-charts/bb-chart/bb-chart-utils';
import { SbSlideToggleComponent } from '../sb-slide-toggle/sb-slide-toggle.component';
import { MarketValuesQuery } from '../../../features/market-values/state/market-values.query';
import { MarketValuesService } from '../../../features/market-values/state/market-values.service';
import { TransactionsQuery } from '../../../features/transactions/state/transactions.query';
import { TransactionsService } from '../../../features/transactions/state/transactions.service';
import { RouterQuery } from '@datorama/akita-ng-router-store';
import { ActivatedRoute, Router } from '@angular/router';
import { ComponentSettings } from '../../../features/user-settings/state/user-setting.model';
import { Instrument, InstrumentType } from '../../state/instruments/instrument.model';
import { UserSettingsService } from '../../../features/user-settings/state/user-settings.service';
import { SbFilterCountPipe } from '../../pipes/sb-filter-count.pipe';
import { SbNotificationBadgeComponent } from '../sb-notification-badge/sb-notification-badge.component';
import { InstrumentsQuery } from '../../state/instruments/instruments.query';
import { TranslateService } from '@ngx-translate/core';
import { InferFilter } from '../../state/query-filter.functions';
import { Transaction } from '../../../features/transactions/state/transaction.model';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';
import { ToastService } from '../../services/toast/toast.service';

@Component({
    selector: 'sb-temp-performance',
    standalone: true,
    imports: [
        CommonModule,
        SbGenericTypeSelectComponent,
        TablerIconsModule,
        SbKeyMetricsComponent,
        SharedModule,
        FormatCurrencyPipe,
        SbBadgesComponent,
        MatTooltipModule,
        SbProTipComponent,
        SbSlideToggleComponent,
        SbFilterCountPipe,
        SbNotificationBadgeComponent
    ],
    templateUrl: './sb-temp-performance.component.html',
    styleUrls: ['./sb-temp-performance.component.scss'],
    animations: [slideInOut, fadeInOut, fadeInOutModal],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SbTempPerformanceComponent implements OnInit, OnDestroy {
    get fitHeightToParent(): boolean {
        return this._fitHeightToParent;
    }

    @Input() set fitHeightToParent(value: boolean) {
        this._fitHeightToParent = value;
    }
    @ViewChild(SbPerformanceChartComponent, { static: false })
    set chartRef(elRef: SbPerformanceChartComponent) {
        if (elRef) {
            this._chartRef = elRef;
        }
    }

    get chartRef(): SbPerformanceChartComponent {
        return this._chartRef;
    }
    private _chartRef: SbPerformanceChartComponent;
    unsubscribe$ = new Subject();
    get showOptions(): boolean {
        return this._showOptions;
    }

    set showOptions(value: boolean) {
        this._showOptions = value;
    }

    get isStandalone(): boolean {
        return this._isStandalone;
    }

    @Input() set isStandalone(value: boolean) {
        this._isStandalone = value;
    }
    constructor(
        private _accountIssuersQuery: AccountIssuersQuery,
        private _legalEntityQuery: LegalEntityQuery,
        private _accountsQuery: AcctsQuery,
        private _userSettingsQuery: UserSettingsQuery,
        private _userSettingsService: UserSettingsService,
        private _chartDataService: ChartDataService,
        private _marketValuesService: MarketValuesService,
        private _marketValuesQuery: MarketValuesQuery,
        private _ui: UiQuery,
        private _benchmarkQuery: BenchmarkQuery,
        private _transactionsQuery: TransactionsQuery,
        private _transactionsService: TransactionsService,
        private _routerQuery: RouterQuery,
        private _router: Router,
        private _route: ActivatedRoute,
        private _instrumentsQuery: InstrumentsQuery,
        private _translateService: TranslateService,
        private _config: AppConfigQuery,
        private _toastService: ToastService
    ) {}

    restrictDashboardChartView = this._config.appConfig.restrictDashboardChartView;

    get activePeriod$(): Observable<Period> {
        return this._activePeriod$.asObservable();
    }

    set activePeriod(value: Period) {
        this._activePeriod$.next(value);
    }

    private _activePeriod$ = new BehaviorSubject<Period>(getPeriodsMap().YTD);

    private _showOptions = false;
    private _isStandalone = true;
    private _fitHeightToParent = false;
    viewportSize$ = this._ui.viewportSize$;
    ccy$ = this._userSettingsQuery.selectGlobalSetting$('BASE_CURRENCY');

    viewOptions = [
        {
            value: 'performance',
            label: 'Performance'
        },
        {
            value: 'marketValue',
            label: 'Market value'
        }
    ];

    viewCtrl = new FormControl(
        this.restrictDashboardChartView
            ? this.viewOptions.find(
                  (option) => option.value === this.restrictDashboardChartView
              )
            : this._routerQuery.getQueryParams('dashboardChartView')
            ? this.viewOptions.find(
                  (option) =>
                      option.value ===
                      this._routerQuery.getQueryParams('dashboardChartView')
              )
            : this.viewOptions[0]
    );

    @Input() set chartView(chartView: (typeof this.viewOptions)[0]) {
        // if the chartView is different from the current value...
        if (chartView.value !== this.viewCtrl.value?.value) {
            // ...set value in next tick using setTimeout
            setTimeout(() => {
                this.viewCtrl.setValue(chartView);
            });
        } else {
            this.viewCtrl.setValue(chartView);
        }
    }

    /** chartView$
     * @param viewCtrl - observable with selected view
     * @returns Observable<{value: string, label: string}> - observable with selected view
     */
    chartView$ = this.viewCtrl.valueChanges.pipe(
        tap((value) => {
            const queryParams = { dashboardChartView: value.value };
            this._router.navigate([], {
                relativeTo: this._route,
                queryParams,
                queryParamsHandling: 'merge',
                replaceUrl: true
            });
        }),
        startWith(this.viewCtrl.value),
        debounceTime(0), // prevent first value from being emitted if another one is set immediately
        distinctUntilChanged(),
        shareReplay(1)
    );

    periodOrderFn = (period: any) => period.order;
    performanceChartOptions: {
        grid?: { hideGridlines?: boolean };
        legend?: { position?: 'top-left' };
    } = { legend: { position: 'top-left' }, grid: { hideGridlines: true } };

    isLoading$ = new ReplaySubject<boolean>(1);

    availableBenchmarks$ = this._benchmarkQuery.selectAvailableBenchmarks$.pipe(
        map((benchmarks) => {
            const currentSelection = this.benchmarksCtrl.value;
            return benchmarks.map(
                (benchmark): Badge => ({
                    title: benchmark.id, // fallback to type if translation is not available
                    outline: !currentSelection.includes(benchmark.id),
                    data: benchmark
                })
            );
        }),
        shareReplay(1)
    );
    benchmarksCtrl = new FormControl<Array<Benchmark['id']>>([]);
    activeBenchmarks$ = this.benchmarksCtrl.valueChanges.pipe(
        tap((_) => this.isLoading$.next(true)),
        debounceTime(1000),
        shareReplay(1)
    );

    /** FILTERS START
     * The following section lists things that this component can be filtered on which currently includes
     * - issuers
     * - legal entities
     */
    availableIssuers$ = combineLatest([
        this._accountsQuery.selectAvailableAccountIssuers$,
        this._accountIssuersQuery.allAsObject$.pipe(
            filter((issuers) => Object.keys(issuers).length > 0)
        )
    ]).pipe(
        map(([issuerIds, issuers]) => {
            const currentSelection = this.issuersCtrl.value;

            return issuerIds
                .map(
                    (id) =>
                        issuers[id] || {
                            displayName: 'Unknown',
                            accountIssuerShortName: 'Unknown',
                            accountIssuerLongName: 'Unknown',
                            accountIssuerId: null
                        }
                )
                .map(
                    (issuer): Badge => ({
                        title:
                            issuer['displayName'] ||
                            issuer.accountIssuerShortName ||
                            issuer.accountIssuerLongName,
                        outline: !currentSelection.includes(issuer.accountIssuerId),
                        data: issuer
                    })
                );
        })
    );
    issuersCtrl = new FormControl<Array<AccountIssuer['accountIssuerId']>>([]);
    activeIssuers$ = defer(() =>
        this.issuersCtrl.valueChanges.pipe(
            startWith(this.issuersCtrl.value),
            distinctUntilArrayItemChanged(),
            shareReplay(1)
        )
    );

    availableLegalEntities$ = combineLatest([
        this._accountsQuery.selectAvailableLegalEntities$,
        this._legalEntityQuery.allAsObject$
    ]).pipe(
        map(([ownerIds, owners]) => {
            const currentSelection = this.legalEntitiesCtrl.value;
            return ownerIds
                .map(
                    (id) =>
                        owners[id] || {
                            displayName: 'Unknown',
                            shortName: 'Unknown',
                            longName: 'Unknown',
                            ownerId: null
                        }
                )
                .map(
                    (owner): Badge => ({
                        title: owner['displayName'] || owner.shortName || owner.longName,
                        outline: !currentSelection.includes(owner.ownerId),
                        data: owner
                    })
                );
        })
    );
    legalEntitiesCtrl = new FormControl<Array<LegalEntity['ownerId']>>([]);
    activeLegalEntities$ =
        this._legalEntityQuery.active$; /*this.legalEntitiesCtrl.valueChanges.pipe(
        debounceTime(300),
        startWith(this.legalEntitiesCtrl.value),
        distinctUntilArrayItemChanged(),
        shareReplay(1)
    );*/

    filterInstrumentTypeCtrl = new FormControl<Array<Instrument['type']>>([]);

    private _availableInstrumentTypes$ = this._instrumentsQuery
        .selectAvailableInstrumentTypes$({
            type: {
                nin: [
                    'COMPANY'
                    /*'CASH',
                    'INDEX',
                    'PE_CONVERTIBLE',
                    'SUBSCRIPTIONRIGHT',
                    'FXFORWARD',
                    'FXSPOT',
                    'CONVERTIBLE',
                    'FUTURE',
                    'ETF'*/
                ]
            }
        })
        .pipe(shareReplay(1));

    availableInstrumentTypes$ = combineLatest([
        this._availableInstrumentTypes$,
        this._translateService.currentLoader.getTranslation('en')
    ]).pipe(
        debounceTime(0),
        map(([types, translations]) => {
            const currentSelection = this.filterInstrumentTypeCtrl.value;
            return types.map(
                (type): Badge => ({
                    title: translations[type] || type || 'Not set', // fallback to type if translation is not available
                    outline: !currentSelection.includes(type),
                    data: type
                })
            );
        })
    );

    /** filteredInstrumentIds$
     * @description list of instrument ids to use to filter data based on selected instrument types
     * @returns Observable<Array<Instrument['instrumentId']>> - observable with instrument ids to use to filter data
     */
    filteredInstrumentIds$ = this._userSettingsQuery.isLoading$.pipe(
        filter((isLoading) => isLoading === false),
        take(1),
        switchMap(() =>
            this.filterInstrumentTypeCtrl.valueChanges.pipe(
                startWith(this.filterInstrumentTypeCtrl.value),
                debounceTime(0),
                distinctUntilArrayItemChanged(),
                map((types) => {
                    return {
                        type: {
                            in: [...types] as Array<Instrument['type']>
                            //ne: 'CASH' as Instrument['type']
                        }
                    };
                }),
                switchMap((filter) =>
                    this._instrumentsQuery.selectInstrumentsByFilter$(filter)
                ),
                map((instruments) =>
                    instruments.map((instrument) => instrument.instrumentId)
                ),
                shareReplay(1)
            )
        )
    );

    /** filteredAccountIds$
     * @description list of account ids to use to filter data based on selected issuers and legal entities
     * @param activeIssuers$ - observable with id list of selected issuers
     * @param activeLegalEntities$ - observable with id list of selected legal entities
     * @returns Observable<Array<Acct['id']>> - observable with account ids to use to filter data
     */
    filteredAccountIds$ = defer(() =>
        combineQueries([
            this.activeIssuers$.pipe(
                map((issuers) =>
                    issuers.length > 0 ? { accountIssuerId: { in: issuers } } : {}
                )
            ),
            this.activeLegalEntities$.pipe(
                map(
                    (legalEntities) =>
                        // Always filter on legal entities, i.e. if none are selected no positions will be shown
                        //legalEntities.length > 0
                        //?
                        ({
                            ownerId: {
                                in: legalEntities.map(
                                    (legalEntity) => legalEntity.ownerId
                                )
                            }
                        })
                    //: {}
                )
            )
        ]).pipe(
            switchMap(([accountIssuerId, ownerId]) =>
                this._accountsQuery.selectAccountsByFilter$({
                    ...accountIssuerId,
                    ...ownerId,
                    accountType: {
                        nin: [AccountTypeEnum.BROKER]
                    }
                })
            ),
            map((accounts) => accounts.map((account) => account.accountId)),
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
            shareReplay(1)
        )
    );

    /** FILTERS END */

    /** performanceSummary$
     * @param filteredAccountIds$ - observable with account ids to use to filter data
     * @param baseCurrency - observable with selected base currency
     * @returns Observable<Array<SbKeyMetric>> - observable with key metrics to display in performance summary
     */
    performanceSummary$ = combineLatest([
        // if filters are disabled, use all accounts except brokers
        this._accountsQuery.isLoading$.pipe(
            filter((isLoading) => isLoading === false),
            take(1),
            switchMap(() =>
                iif(
                    () => this.disableFilters,
                    this._accountsQuery
                        .selectAccountsByFilter$({
                            accountType: {
                                nin: [AccountTypeEnum.BROKER]
                            }
                        })
                        .pipe(
                            map((accounts) =>
                                accounts.map((account) => account.accountId)
                            )
                        ),
                    this.filteredAccountIds$
                ).pipe(distinctUntilArrayItemChanged())
            )
        ),
        this._userSettingsQuery.selectGlobalSetting$('BASE_CURRENCY')
    ]).pipe(
        debounceTime(0),
        switchMap(([accountIds, baseCcy]) =>
            this._chartDataService.getSummary(accountIds, baseCcy)
        ),
        shareReplay(1)
    );

    /** performanceKeyMetrics$
     * @param activePeriod$ - observable with selected period
     * @param performanceSummary$ - observable with performance summary data
     * @param locale - observable with selected locale
     * @param ccy - observable with selected base currency
     * @returns Observable<Array<SbKeyMetric>> - observable with key metrics to display in performance key metrics
     */
    performanceKeyMetrics$ = combineLatest([
        this.activePeriod$,
        this.performanceSummary$,
        this._userSettingsQuery.selectGlobalSetting$('LOCALE'),
        this._userSettingsQuery.selectGlobalSetting$('BASE_CURRENCY')
    ]).pipe(
        map(([period, periods, locale, ccy]) => {
            // Period length in number of years. For example 0.75 means 9 months.
            const MS_PER_YEAR = 1000 * 60 * 60 * 24 * 365.25;

            //FIXME: Retreive Rf from backend for the given period. In the real implementation we will assign an index for retrieving the risk free rate Rf for the calculated period, so that different Rf are used for different report periods etc.
            // For example STIBOR T/N for SEK and EURIBOR for EUR etc.
            const riskFreeReturn = 0.02246; // STIBOR T/N as per 2025-04-03

            // FIXME: @Robert: Start date for case when period is All needs to be set properly
            const performanceStartDate =
                period.start != null ? new Date(period.start) : new Date('2019-12-31');

            const periodLengthYears =
                Math.abs(
                    new Date(period.end).getTime() -
                        new Date(performanceStartDate).getTime()
                ) / MS_PER_YEAR;

            return [
                {
                    title: 'Market value',
                    value: periods.marketValue.value,
                    change: periods[period.name]?.relative /*calculateReturn(
                        periods.marketValue?.value,
                        periods[period.name]?.absolute
                    ),*/,
                    addColor: true,
                    ccy,
                    maximumFractionDigits: 0,
                    description:
                        'Current market value and true time weighted return for selected period.'
                },
                {
                    title: 'Period',
                    value: period.name,
                    description: `${formatDate(
                        period.start,
                        'shortDate',
                        locale
                    )} - ${formatDate(period.end, 'shortDate', locale)} (${period.name})`,
                    type: 'text'
                },
                {
                    title: 'Volatility',
                    value: periods[period.name]?.volatility,
                    formatStyle: 'percent',
                    description: 'Volatility during selected period.'
                },
                {
                    title: 'Absolute',
                    value: periods[period.name]?.absolute,
                    prefixPositiveValue: true,
                    addColor: true,
                    maximumFractionDigits: 0,
                    ccy,
                    description: 'Absolute return for selected period.'
                }
                /*{
                    title: 'Sharpe Ratio',
                    value:
                        ((1 / periodLengthYears) * periods[period.name]?.relative -
                            riskFreeReturn) /
                        periods[period.name]?.volatility,
                    prefixPositiveValue: true,
                    addColor: true,
                    maximumFractionDigits: 2,
                    description: 'Sharpe ratio during selected period (annualized).',
                }*/

                /*{
                    title: 'Relative',
                    value: periods[period.name].relative,
                    prefixPositiveValue: true,
                    addColor: true,
                    formatStyle: 'percent',
                    description: 'Relative return for selected period.'
                }*/
            ] as SbKeyMetric[];
        }),
        shareReplay(1)
    );

    /** performanceChartData$
     * @param filteredAccountIds$ - observable with account ids to use to filter data
     * @param activePeriod$ - observable with selected period
     * @param baseCurrency - observable with selected base currency
     * @returns Observable<Array<ChartData>> - observable with chart data to display in performance chart
     */
    performanceChartData$ = combineQueries([
        // if filters are disabled, use all accounts except brokers
        this._accountsQuery.isLoading$.pipe(
            filter((isLoading) => isLoading === false),
            take(1),
            switchMap(() =>
                iif(
                    () => this.disableFilters,
                    this._accountsQuery
                        .selectAccountsByFilter$({
                            accountType: {
                                nin: [AccountTypeEnum.BROKER]
                            }
                        })
                        .pipe(
                            map((accounts) =>
                                accounts.map((account) => account.accountId)
                            )
                        ),
                    this.filteredAccountIds$
                ).pipe(distinctUntilArrayItemChanged())
            )
        ),
        // this.filteredInstrumentIds$,
        this.activePeriod$,
        this._userSettingsQuery.selectGlobalSetting$('BASE_CURRENCY')
    ]).pipe(
        tap((period) => {
            this.isLoading$.next(true);
        }),
        debounceTime(0),
        switchMap(([accountIds, period, baseCurrency]) => {
            if (period.name === 'All') {
                return this._chartDataService
                    .get(accountIds, null, null, null, baseCurrency)
                    .pipe(
                        tap((res) => {
                            this.chartRef.setPeriod({
                                ...period,
                                start: res[0].data[0][0]
                            });
                        })
                    );
            }
            return this._chartDataService
                .get(
                    accountIds,
                    null,
                    // disable passing dates to backend for now as it affects the performance too much, better filter on the frontend until fixed
                    null, // new Date(period.start),
                    null, // new Date(period.end),
                    baseCurrency
                )
                .pipe(
                    tap((res) => {
                        this.chartRef?.setPeriod(period);
                    })
                );
        })
    );

    // chartAxisCtrl = new FormControl();
    chartAxisOptions$ = this.ccy$.pipe(
        map((ccy) => {
            // [{value: 'relative', label: '%', icon: 'percentage' }, { value: 'absolute', label: ccy }]
            return { value: 'absolute', label: ccy };
        })
        // tap((options) => {
        //     if (!this.chartAxisCtrl.value) {
        //         this.chartAxisCtrl.setValue(options[0]);
        //     }
        // })
    );
    // chartAxis$ = this.chartAxisCtrl.valueChanges.pipe(
    //     distinctUntilChanged(),
    //     debounceTime(200),
    //
    //     shareReplay(1)
    // );
    chartAxis$ = this.chartAxisOptions$.pipe(
        map((axis) => ({ type: axis.value, title: axis.label }))
    );

    maxAccountTimestamp$ = this.filteredAccountIds$.pipe(
        switchMap((ids) =>
            this._accountsQuery.selectAccountsByAge$(ids, null, 1).pipe(
                filter((accts) => accts.length > 0),
                map((accts) =>
                    accts[0].openingTimestamp > 0 ? accts[0].openingTimestamp : 0
                )
            )
        ),
        shareReplay(1),
        takeUntil(this.unsubscribe$)
    );

    periodSummary$ = this.maxAccountTimestamp$.pipe(
        switchMap((maxAge) => {
            return this._marketValuesQuery.selectPeriodSummaryForRange$(
                maxAge,
                Date.now()
            );
        }),
        shareReplay(1)
    );

    showEventsCtrl = new FormControl<{ value: boolean }>({ value: false });
    showEvents$ = this.showEventsCtrl.valueChanges.pipe(
        startWith(this.showEventsCtrl.value),
        map((value) => value.value),
        distinctUntilChanged(),
        shareReplay(1)
    );

    // @ts-ignore
    /** events$
     * @description events to display in market value chart
     */
    events$ = this.showEvents$.pipe(
        debounceTime(200),
        switchMap((showEvents) => {
            return showEvents
                ? this._transactionsService.get().pipe(
                      debounceTime(0),
                      switchMap((_) =>
                          combineLatest([
                              this.filteredInstrumentIds$,
                              this.filteredAccountIds$
                          ]).pipe(
                              switchMap(([instrumentIds, accountIds]) => {
                                  let filter: InferFilter<Transaction> = {
                                      transactionType: {
                                          in: ['TRANSFER']
                                      },
                                      subCategory: {
                                          in: ['EXTERNAL']
                                      },
                                      instrument: {
                                          type: {
                                              in: ['CASH']
                                          }
                                      }
                                  };
                                  if (instrumentIds.length > 0) {
                                      filter.instrument = {
                                          ...filter.instrument,
                                          // @ts-ignore
                                          instrumentId: {
                                              in: instrumentIds
                                          }
                                      };
                                  }
                                  if (accountIds.length > 0) {
                                      filter = {
                                          ...filter,
                                          accountIds: {
                                              // @ts-ignore
                                              fn: (
                                                  accounts: Array<Acct['accountId']>
                                              ) => {
                                                  return accountIds.some((accountId) =>
                                                      accounts.includes(accountId)
                                                  );
                                              }
                                          }
                                      };
                                  }
                                  return this._transactionsQuery.selectTransactionByFilter$(
                                      filter
                                  );
                              }),
                              map((transactions) => {
                                  return transactions.reduce((acc, transaction) => {
                                      const event =
                                          transaction.nominal > 0
                                              ? 'eventBought'
                                              : 'eventSold';
                                      let title;
                                      switch (transaction.transactionType) {
                                          case 'TRANSFER':
                                              title =
                                                  event === 'eventBought'
                                                      ? 'Deposit'
                                                      : 'Withdrawal';
                                              break;
                                          default:
                                              title = 'Unknown';
                                              break;
                                      }
                                      if (!acc[event]) {
                                          acc[event] = {};
                                      }
                                      if (!acc[event][transaction.transactionTimestamp]) {
                                          acc[event][transaction.transactionTimestamp] = {
                                              title,
                                              price: transaction.transactionPrice,
                                              qty: transaction.nominal
                                          };
                                      }
                                      return acc;
                                  }, {});
                              })
                          )
                      )
                  )
                : of({});
        }),
        shareReplay(1)
    );

    /** marketValueKeyMetrics$
     * @param activePeriod$ - observable with selected period
     * @param periodSummary$ - observable with period summary data
     * @param locale - observable with selected locale
     * @param ccy - observable with selected base currency
     * @returns Observable<Array<SbKeyMetric>> - observable with key metrics to display in market value key metrics
     */
    marketValueKeyMetrics$ = combineLatest([
        this.activePeriod$,
        this.periodSummary$,
        this._userSettingsQuery.selectGlobalSetting$('LOCALE'),
        this.ccy$
    ]).pipe(
        map(([{ name, start, end }, periods, locale, ccy]) => {
            return [
                {
                    title: 'Market value',
                    value: periods[name]?.marketValue,
                    change: [
                        periods[name]?.absoluteChange,
                        periods[name]?.relativeChange
                    ],
                    prefixPositiveValue: false,
                    addColor: true,
                    maximumFractionDigits: 4,
                    ccy,
                    description:
                        'Latest market value as well as change during selected period.'
                },
                /*{
                    title: 'Total market value',
                    value: periods.marketValue.value,
                    change: periods[period.name].relative,
                    addColor: true,
                    ccy,
                    maximumFractionDigits: 0,
                    description:
                        'Current total market value and true time weighted return for selected period.'
                },*/
                {
                    title: 'High',
                    value: periods[name]?.high,
                    description: 'Highest market value during selected period.'
                },
                {
                    title: 'Low',
                    value: periods[name]?.low,
                    description: 'Lowest market value during selected period.'
                },
                {
                    title: 'Period',
                    value: name,
                    description: `${formatDate(
                        start,
                        'shortDate',
                        locale
                    )} - ${formatDate(end, 'shortDate', locale)} (${name})`,
                    type: 'text'
                }

                // SWIM-6577: Remove volatility metric as it does not make sense to display for market value
                // {
                //     title: 'Volatility',
                //     value: periods[name]?.volatility,
                //     formatStyle: 'percent',
                //     description: 'Volatility for market value during selected period.'
                // },
            ] as SbKeyMetric[];
        }),
        shareReplay(1)
    );

    /** marketValueData$
     * @param filteredAccountIds$ - observable with account ids to use to filter data
     * @param activePeriod$ - observable with selected period
     * @param baseCurrency - observable with selected base currency
     * @returns Observable<Array<ChartData>> - observable with chart data to display in market value chart
     */
    marketValueData$ = combineQueries([
        this._accountsQuery.isLoading$.pipe(
            filter((isLoading) => isLoading === false),
            take(1),
            switchMap(() =>
                this.filteredAccountIds$.pipe(distinctUntilArrayItemChanged())
            )
        ),
        this._instrumentsQuery.isLoading$.pipe(
            filter((isLoading) => isLoading === false),
            take(1),
            switchMap(() =>
                this.filteredInstrumentIds$.pipe(distinctUntilArrayItemChanged())
            )
        ),
        this.activePeriod$,
        this._userSettingsQuery.selectGlobalSetting$('BASE_CURRENCY'),
        this.maxAccountTimestamp$
    ]).pipe(
        tap((_) => {
            this.isLoading$.next(true);
        }),
        debounceTime(0),
        switchMap(([accountIds, instrumentIds, period, baseCurrency, maxTimestamp]) => {
            return this._marketValuesService
                .getV2({
                    accountIds,
                    instrumentIds,
                    baseCurrency,
                    fromDate: new Date(maxTimestamp),
                    toDate: period.end
                })
                .pipe(
                    switchMap((res) => {
                        let selection = { ...period };
                        if (period.name === 'All') {
                            selection = {
                                ...selection,
                                start: new Date(res[0].valueDate)
                            };
                        }
                        this.chartRef?.setPeriod(selection);
                        return this._marketValuesQuery.selectChartDataForRange$(
                            maxTimestamp < selection.start.getTime()
                                ? maxTimestamp
                                : selection.start.getTime(),
                            selection.end.getTime()
                        );
                    })
                );
        })
    );

    _previousNumberOfEventsTypes = 0;
    /** marketValueChartOptions$
     * @param ccy - observable with selected base currency
     * @param locale - observable with selected locale
     * @param _eventData - observable with transaction data
     * @returns Observable<ChartOptions> - observable with chart options for market value chart
     */
    marketValueChartOptions$ = combineLatest([
        this._userSettingsQuery.selectGlobalSetting$('BASE_CURRENCY'),
        this._userSettingsQuery.selectGlobalSetting$('LOCALE'),
        this.events$
    ]).pipe(
        map(([ccy, locale, _eventData]) => {
            const config = {
                // only reset chart if events have new series that were not present in the previous data set
                resetChart:
                    Object.keys(_eventData).length > this._previousNumberOfEventsTypes,
                legend: { position: 'top-left' },
                grid: { hideGridlines: true },
                currency: ccy,
                tooltip: {
                    contents: function (this, data) {
                        const benchmarks = {
                            OMX: 'OMX',
                            SIX: 'SIX',
                            'S&P': 'S&P'
                        };
                        const series = data.reduce((acc, { name, value }, index) => {
                            const serieIndex =
                                this.data().findIndex((s) => s.id === name) + 1;
                            const color = `--bb-chart-serie-${serieIndex}-color-1`;
                            const isBenchmark = name.indexOf('_benchmark_') !== -1;
                            let classNames = '';
                            let html =
                                this.data()[serieIndex]?.values[data[0].index].value !==
                                null
                                    ? `<div class="column${classNames}" style="--color: var(${color})">
            <dt>${fromCamelCaseToSentence(name.replace(/_benchmark_/, ''))}</dt>
            <dd><strong>${formatCurrency(
                this.data()[serieIndex]?.values[data[0].index].value,
                locale,
                ccy,
                null,
                '0.0-2'
            )}</strong> (${formatPercent(value, locale, '0.0-2')})
                </dd>
        </div>`
                                    : `<div class="column${classNames}" style="--color: var(${color})">
            <dt>${fromCamelCaseToSentence(name.replace(/_benchmark_/, ''))}</dt>
            <dd><strong>No market price for date</strong>
                </dd>
        </div>`;
                            switch (name) {
                                case 'eventBought':
                                case 'eventSold':
                                    if (value === null) {
                                        html = '';
                                    } else {
                                        const pointUtc = new Date(data[0].x).getTime();
                                        const eventData = _eventData[name]
                                            ? _eventData[name][pointUtc]
                                            : {};
                                        classNames =
                                            name === 'eventBought'
                                                ? ' buy-color'
                                                : ' sell-color';
                                        html = `<div class="column${classNames}" style="--color: var(${color})">
            <dt>${
                eventData?.title ?? fromCamelCaseToSentence(name.replace(/event/, ''))
            }</dt>
            <dd class="text-right">Qty: <strong>${
                eventData?.qty
            }</strong> at <strong>${formatCurrency(
                                            eventData?.price,
                                            locale,
                                            ccy,
                                            null,
                                            '0.0-2'
                                        )}</strong>
            <br/>=
            <strong>${formatCurrency(
                eventData?.price * eventData?.qty,
                locale,
                ccy,
                null,
                '0.0-2'
            )}</strong>
                </dd>
        </div>`;
                                        if (
                                            eventData.title === 'Deposit' ||
                                            eventData.title === 'Withdrawal'
                                        ) {
                                            html = `<div class="column${classNames}" style="--color: var(${color})">
            <dt>${eventData.title}</dt>
            <dd class="text-right">
            <strong>${formatCurrency(
                eventData.price * eventData.qty,
                locale,
                ccy,
                null,
                '0.0-2'
            )}</strong>
                </dd>
        </div>`;
                                        }
                                    }
                                    break;
                                default:
                                    break;
                            }
                            if (isBenchmark) {
                                const benchmarkIndex =
                                    Object.keys(benchmarks).findIndex(
                                        (benchmark) => name.indexOf(benchmark) !== -1
                                    ) + 1;
                                classNames = ` benchmark-color benchmark-color-${benchmarkIndex}`;
                                html = `<div class="column${classNames}" style="--color: var(${color})">
            <dt>${fromCamelCaseToSentence(name.replace(/_benchmark_/, ''))}</dt>
            <dd>${formatPercent(value, locale, '0.0-2')}
                </dd>
        </div>`;
                            }
                            return acc + html;
                        }, '');
                        return `
<figure class="bb-tooltip bb-tooltip-date">
    <figcaption>${formatDate(data[0].x, 'fullDate', locale)}</figcaption>
    <dl>
    ${series}
    </dl>
</figure>
`;
                    }
                }
            };
            this._previousNumberOfEventsTypes = Object.keys(_eventData).length;
            return config;
        }),
        shareReplay(1)
        /*distinctUntilChanged((prev, curr) => {
            return JSON.stringify(prev) !== JSON.stringify(curr);
        })*/
    );
    disableFilters: boolean = false; //this._routerQuery.getQueryParams('enableChartFilters') !== 'true';

    onLoadingStateChange(isLoading: boolean) {
        this.isLoading$.next(isLoading);
    }

    updatePeriod(period: any) {
        this.activePeriod = getPeriodsMap()[period.name];
    }

    trackByFn(index: number, item: any) {
        return item.name;
    }

    toggleShowOptions() {
        this.showOptions = !this.showOptions;
    }

    toggleBenchmarks($event: BadgesChangeEvent<Benchmark>) {
        if ($event.type === 'toggle-active') {
            this.benchmarksCtrl.setValue(
                $event.badges
                    .filter((badge) => !badge.outline)
                    .map((badge) => badge.data.id)
            );
        }
    }

    ngOnInit() {
        // select all issuers by default
        combineLatest([
            this._userSettingsQuery.selectComponentSetting$(
                'ISSUER_FILTER',
                'sb-market-value-chart'
            ),
            this.availableIssuers$.pipe(filter((types) => types.length > 0))
        ])
            .pipe(
                tap(([persisted, available]) => {
                    if (persisted && persisted.length > 0) {
                        this.issuersCtrl.setValue(persisted);
                    } else {
                        this.issuersCtrl.setValue(
                            available.map((issuer) => issuer.data.accountIssuerId)
                        );
                    }
                }),
                take(1),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();

        this._accountsQuery.selectAvailableLegalEntities$
            .pipe(
                debounceTime(100),
                tap((ownersIds) => {
                    this.legalEntitiesCtrl.setValue(ownersIds);
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();

        // select all instrument types by default
        combineLatest([
            this._userSettingsQuery.selectComponentSetting$(
                'INSTRUMENT_TYPE_FILTER',
                'sb-market-value-chart'
            ),
            this._availableInstrumentTypes$.pipe(filter((types) => types.length > 0))
        ])
            .pipe(
                tap(([persisted, available]) => {
                    if (persisted && persisted.length > 0) {
                        this.filterInstrumentTypeCtrl.setValue(persisted);
                    } else {
                        this.filterInstrumentTypeCtrl.setValue(available);
                    }
                }),
                take(1),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();
    }

    onBenchmarksChange($event: Array<Benchmark['id']>) {
        if ($event !== this.benchmarksCtrl.value) {
            this.benchmarksCtrl.setValue($event);
        }
    }

    filterIssuers($event: BadgesChangeEvent<AccountIssuer>) {
        if ($event.type === 'toggle-active') {
            this.issuersCtrl.setValue(
                $event.badges
                    .filter((badge) => !badge.outline)
                    .map((badge) => badge.data.accountIssuerId)
            );
            this.saveFilter({ setting: 'ISSUER_FILTER', filter: this.issuersCtrl.value });
        } else if ($event.type === 'not-allowed') {
            this._toastService.show({
                intent: 'info',
                message: $event.message,
                duration: 3000
            });
        }
    }

    /* TODO: remove once global filter for legal entities is implemented and released
    filterLegalEntities($event: BadgesChangeEvent<LegalEntity>) {
        if ($event.type === 'toggle-active') {
            this.legalEntitiesCtrl.setValue(
                $event.badges
                    .filter((badge) => !badge.outline)
                    .map((badge) => badge.data.ownerId)
            );
            this.saveFilter({
                setting: 'LEGAL_ENTITY_FILTER',
                filter: this.legalEntitiesCtrl.value
            });
        }
    }*/

    filterInstrumentTypes($event: BadgesChangeEvent<InstrumentType>) {
        if ($event.type === 'toggle-active') {
            this.filterInstrumentTypeCtrl.setValue(
                $event.badges.filter((badge) => !badge.outline).map((badge) => badge.data)
            );
            this.saveFilter({
                setting: 'INSTRUMENT_TYPE_FILTER',
                filter: this.filterInstrumentTypeCtrl.value
            });
        } else if ($event.type === 'not-allowed') {
            this._toastService.show({
                intent: 'info',
                message: $event.message,
                duration: 3000
            });
        }
    }

    saveFilter({
        setting,
        filter
    }: {
        setting: keyof ComponentSettings;
        filter:
            | Array<LegalEntity['ownerId']>
            | Array<Instrument['type']>
            | Array<Acct['accountType']>
            | Array<AccountIssuer['accountIssuerId']>;
    }) {
        this._userSettingsService
            .saveComponentSetting(setting, filter, 'sb-market-value-chart', true)
            .subscribe();
    }

    ngOnDestroy() {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
